import * as React from 'react';
import { Button } from 'react-bootstrap';
import SentryLogger from '../../helpers/SentryLogger';
import { markForcedExit } from '../../helpers/pageExit';

interface Props {
    children: React.ReactNode;
    fallback?: React.ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: React.ErrorInfo | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null
        };
    }

    static getDerivedStateFromError(error: Error): Partial<State> {
        return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        console.error('[ErrorBoundary]: Caught error', error, errorInfo);

        this.setState({
            error,
            errorInfo
        });

        // Report to Sentry
        SentryLogger.logError('ErrorBoundary', error, {
            componentStack: errorInfo.componentStack
        });
    }

    handleReset = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null
        });
    };

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div className="error-boundary">
                    <h2>Something went wrong</h2>
                    <p className="error-boundary__message">
                        {this.state.error?.message || 'An unexpected error occurred'}
                    </p>
                    {process.env.NODE_ENV === 'development' && this.state.errorInfo && (
                        <details className="error-boundary__details">
                            <summary>Stack Trace</summary>
                            <pre>{this.state.errorInfo.componentStack}</pre>
                        </details>
                    )}
                    <div className="error-boundary__actions">
                        <Button variant="primary" onClick={this.handleReset}>
                            Try Again
                        </Button>
                        <Button variant="default" onClick={() => {
                            markForcedExit('error-screen-go-to-jobs');
                            window.location.href = '/jobs';
                        }}>
                            Return to Jobs
                        </Button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
